/* eslint-disable no-unused-vars */
/* PartnerRegistrationForm.jsx
   Single-file, responsive wizard form
   - Tailwind-only UI
   - Framer Motion animations (fade + slide)
   - Desktop: left sidebar; Mobile: bottom stepper
   - Firebase hooks preserved (db, storage, getAuth)
*/

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Upload,
  Trash2,
  Loader,
  X,
  Image as ImageIcon,
  Menu,
  Check,
} from "lucide-react";
import { Link } from "react-router-dom";

// Firebase imports you already use
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRefFn, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { db, storage } from "../firebase";

/* -------------------------
   Utility helpers
--------------------------*/

// Get current user safely
export const getCurrentUser = () => {
  try {
    const auth = getAuth();
    return auth.currentUser || null;
  } catch (err) {
    console.warn("getCurrentUser error:", err);
    return null;
  }
};

// Upload image to storage and return download URL
export const uploadImageToStorage = async (file, path) => {
  if (!file) throw new Error("No file provided");
  if (!storage) throw new Error("Firebase storage not initialized");
  const safePath = path ? `${path}` : "uploads";
  const fileRef = storageRefFn(storage, `${safePath}/${Date.now()}_${file.name}`);
  const snapshot = await uploadBytes(fileRef, file);
  const url = await getDownloadURL(snapshot.ref);
  return url;
};

// Detect location using navigator + Nominatim reverse geocode
export const detectAndFillLocation = (scope, setFormData, setUploadProgress = () => { }, showToast = () => { }) => {
  if (!navigator.geolocation) {
    showToast("Geolocation not supported", "error");
    return;
  }
  setUploadProgress("Detecting location...");
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      const lat = pos.coords.latitude;
      const lon = pos.coords.longitude;
      try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&accept-language=en`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Reverse geocoding failed");
        const data = await res.json();
        const display = data.display_name || "";
        const addr = data.address || {};
        const city = addr.city || addr.town || addr.village || addr.county || "";
        const state = addr.state || "";
        const country = addr.country || "";

        if (scope === "basic") {
          setFormData((prev) => ({
            ...prev,
            location: {
              ...prev.location,
              address: display,
              city,
              state,
              country,
              latitude: lat,
              longitude: lon,
            },
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            businessDetails: {
              ...prev.businessDetails,
              venueLocation: {
                ...prev.businessDetails.venueLocation,
                address: display,
                city,
                state,
                country,
                latitude: lat,
                longitude: lon,
              },
            },
          }));
        }
        setUploadProgress("");
        showToast("Location detected", "success");
      } catch (err) {
        console.error("Reverse geocoding error:", err);
        setUploadProgress("");
        showToast("Unable to determine address", "error");
      }
    },
    (err) => {
      console.error("Geolocation error", err);
      setUploadProgress("");
      if (err.code === 1) showToast("Permission denied. Allow location access.", "error");
      else showToast("Unable to fetch location. Try again.", "error");
    },
    { enableHighAccuracy: true, timeout: 10000 }
  );
};

/* -------------------------
   Small presentational subcomponents
--------------------------*/

// Tiny accessible input wrapper
const Field = ({ label, required, children, hint }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-[12px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

// Sidebar (desktop)
const Sidebar = ({ steps, currentStep }) => {
  return (
    <aside className="hidden md:flex md:flex-col md:w-72 bg-white border-r p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-orange-500">EventPlanly</h1>
        <p className="text-xs text-gray-500 mt-1">Partner Registration</p>
      </div>

      <nav className="flex-1">
        <ul className="space-y-4">
          {steps.map((s, i) => (
            <li key={s} className="flex items-start gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${i === currentStep ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-700"
                  }`}
                aria-hidden
              >
                {i + 1}
              </div>
              <div>
                <div className={`text-sm font-medium ${i === currentStep ? "text-orange-600" : "text-gray-700"}`}>{s}</div>
                {i < currentStep && <div className="text-[12px] text-gray-400">Completed</div>}
              </div>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mt-6">
        <div className="text-sm text-gray-500">Need help?</div>
        <div className="text-xs text-gray-400 mt-1">Contact support from the dashboard</div>
      </div>
    </aside>
  );
};

// Mobile bottom stepper
const MobileStepper = ({ steps, currentStep, onJump }) => (
  <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[95%] md:hidden">
    <div className="bg-white shadow rounded-lg p-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 overflow-x-auto">
        {steps.map((s, i) => (
          <button
            key={s}
            onClick={() => onJump(i)}
            className={`px-3 py-1 rounded-full text-xs whitespace-nowrap font-medium ${i === currentStep ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-700"
              }`}
          >
            {s}
          </button>
        ))}
      </div>
      <div className="text-xs text-gray-500">{currentStep + 1}/{steps.length}</div>
    </div>
  </div>
);

/* -------------------------
   Main Form Screens
--------------------------*/

// Welcome Screen
const WelcomeScreen = ({ formData, setFormData, next }) => {
  return (
    <motion.div
      key="welcome"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-10"
    >
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="w-full sm:w-2/5 text-center">
          <svg className="w-44 h-auto mx-auto" viewBox="0 0 320 220" fill="none" aria-hidden>
            <rect x="120" y="140" width="120" height="10" rx="2" fill="#D4A574" />
            <circle cx="230" cy="50" r="18" fill="#FF9500" />
          </svg>
        </div>

        <div className="w-full sm:w-3/5">
          <h2 className="text-2xl font-bold text-orange-500 mb-2">Hey Partner — let’s set up your profile!</h2>
          <p className="text-gray-600 mb-4">Complete your account setup by filling required fields. This helps users discover your services.</p>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.agreedToTerms}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, agreedToTerms: e.target.checked }))
              }
              className="w-4 h-4 rounded border-gray-300 accent-orange-500"
            />

            <span className="text-sm text-gray-700">
              I agree to the{" "}
              <Link to="/terms-and-conditions" className="underline text-orange-500">
                Terms & Conditions
              </Link>
            </span>
          </label>

          <div className="mt-6 flex gap-3">
            <button
              onClick={() => {
                if (!formData.agreedToTerms) {
                  alert("Please agree to Terms & Conditions to continue");
                  return;
                }
                next(1);
              }}
              className="ml-auto bg-orange-500 text-white px-5 py-2 rounded-lg font-semibold hover:bg-orange-600"
            >
              Let's Start
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Basic Details
const BasicDetailsScreen = ({ formData, setFormData, errors, setErrors, next, detectLocation, showToast }) => {
  const addressRef = useRef(null);

  const syncAddress = () => {
    const val = addressRef.current?.value || "";
    setFormData((prev) => ({ ...prev, location: { ...prev.location, address: val } }));
  };

  return (
    <motion.div
      key="basic"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -8 }}
      className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-10"
    >
      <h3 className="text-xl font-bold text-orange-500 mb-3">Basic Details</h3>
      <p className="text-gray-600 mb-6">Tell us about you so clients know who they are booking.</p>

      <div className="flex flex-col sm:flex-row gap-6">
        <div className="w-full sm:w-1/3 flex flex-col items-center">
          <div className="w-28 h-28 rounded-full border-4 border-orange-500 overflow-hidden bg-gray-100 flex items-center justify-center">
            {formData.imagePreview ? (
              <img src={formData.imagePreview} alt="profile" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="text-gray-400" size={36} />
            )}
          </div>
          <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-sm text-orange-500">
            <Upload size={16} /> Upload
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) {
                  showToast("Image must be < 5MB", "error");
                  return;
                }
                setFormData((prev) => ({ ...prev, image: file, imagePreview: URL.createObjectURL(file) }));
              }}
              className="hidden"
            />
          </label>
        </div>

        <div className="w-full sm:w-2/3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name" required>
              <input
                value={formData.fullName}
                onChange={(e) => setFormData((prev) => ({ ...prev, fullName: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${errors.fullName ? "border-red-400" : "border-gray-300"}`}
                placeholder="Rahul Kumar"
              />
              {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
            </Field>

            <Field label="Email" required>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${errors.email ? "border-red-400" : "border-gray-300"}`}
                placeholder="name@email.com"
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </Field>

            <Field label="Gender" required>
              <div className="flex gap-2">
                {["Male", "Female", "Others"].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, gender: g }))}
                    className={`px-3 py-2 rounded-lg border ${formData.gender === g ? "border-orange-500 bg-orange-50 text-orange-600" : "border-gray-300 text-gray-700"}`}
                  >
                    {g}
                  </button>
                ))}
                {errors.gender && <p className="text-xs text-red-500 mt-1">{errors.gender}</p>}
              </div>
            </Field>

            <Field label="Mobile" required>
              <input
                value={formData.mobile}
                onChange={(e) => setFormData((prev) => ({ ...prev, mobile: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${errors.mobile ? "border-red-400" : "border-gray-300"}`}
                placeholder="9999999999"
              />
              {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
            </Field>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Address <span className="text-red-500">*</span></label>
              <button
                onClick={() => detectLocation("basic", setFormData, () => { }, showToast)}
                className="inline-flex items-center gap-2 text-sm px-3 py-1 rounded bg-orange-50 text-orange-600"
              >
                <MapPin size={14} /> Detect My Location
              </button>
            </div>
            <textarea
              ref={addressRef}
              defaultValue={formData.location.address}
              onBlur={() => {
                const val = addressRef.current?.value || "";
                setFormData((prev) => ({ ...prev, location: { ...prev.location, address: val } }));
              }}
              rows={3}
              placeholder="Full address"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${errors.location ? "border-red-400" : "border-gray-300"}`}
            />
            {errors.location && <p className="text-xs text-red-500 mt-1">{errors.location}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <input
              value={formData.location.city}
              onChange={(e) => setFormData((prev) => ({ ...prev, location: { ...prev.location, city: e.target.value } }))}
              placeholder="City"
              className="px-3 py-2 border rounded-lg focus:outline-none border-gray-300"
            />
            <input
              value={formData.location.state}
              onChange={(e) => setFormData((prev) => ({ ...prev, location: { ...prev.location, state: e.target.value } }))}
              placeholder="State"
              className="px-3 py-2 border rounded-lg focus:outline-none border-gray-300"
            />
            <input
              value={formData.location.country}
              onChange={(e) => setFormData((prev) => ({ ...prev, location: { ...prev.location, country: e.target.value } }))}
              placeholder="Country"
              className="px-3 py-2 border rounded-lg focus:outline-none border-gray-300"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex gap-3 justify-end">
        <button
          onClick={() => next(0)}
          className="px-4 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50"
        >
          Cancel
        </button>
        <button
          onClick={() => {
            // Validate step
            const e = {};
            if (!formData.fullName.trim()) e.fullName = "Name required";
            if (!formData.email.trim()) e.email = "Email required";
            if (!formData.gender.trim()) e.gender = "Gender required";
            if (!formData.mobile.trim()) e.mobile = "Mobile required";
            if (!formData.location.address.trim()) e.location = "Address required";
            if (Object.keys(e).length) {
              setErrors(e);
              alert("Please correct the highlighted fields.");
              return;
            }
            setErrors({});
            next(2);
          }}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Save & Continue
        </button>
      </div>
    </motion.div>
  );
};

// Service Details
const ServiceDetailsScreen = ({ formData, setFormData, services, next }) => {
  return (
    <motion.div
      key="services"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -8 }}
      className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-10"
    >
      <h3 className="text-xl font-bold text-orange-500 mb-3">Services You Offer</h3>
      <p className="text-gray-600 mb-4">Select one or more services you provide.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => {
          const active = formData.selectedServices.includes(s.id);
          return (
            <button
              key={s.id}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  selectedServices: active ? prev.selectedServices.filter((id) => id !== s.id) : [...prev.selectedServices, s.id],
                }))
              }
              className={`p-4 rounded-lg text-left border transition ${active ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-orange-300"
                }`}
            >
              <div className="text-2xl mb-2">{s.icon}</div>
              <div className="font-medium text-gray-800">{s.title}</div>
              <div className="text-sm text-gray-500">{s.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex gap-3 justify-end">
        <button onClick={() => next(1)} className="px-4 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50">
          Previous
        </button>
        <button
          onClick={() => {
            if (formData.selectedServices.length === 0) {
              alert("Please select at least one service");
              return;
            }
            next(3);
          }}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Save & Continue
        </button>
      </div>
    </motion.div>
  );
};

// Business Info
const BusinessInformationScreen = ({ formData, setFormData, errors, setErrors, next, detectLocation, showToast }) => {
  const venueRef = useRef(null);
  const syncVenue = () => {
    const val = venueRef.current?.value || "";
    setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, venueLocation: { ...prev.businessDetails.venueLocation, address: val } } }));
  };

  return (
    <motion.div
      key="business"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -8 }}
      className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-10"
    >
      <h3 className="text-xl font-bold text-orange-500 mb-3">Business Information</h3>
      <p className="text-gray-600 mb-4">Share details about your business so clients can trust you.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Business Name" required>
          <input
            value={formData.businessDetails.businessName}
            onChange={(e) => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, businessName: e.target.value } }))}
            placeholder="Business Name"
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${errors.businessName ? "border-red-400" : "border-gray-300"}`}
          />
          {errors.businessName && <p className="text-xs text-red-500 mt-1">{errors.businessName}</p>}
        </Field>

        <Field label="Website / Instagram" required>
          <input
            value={formData.businessDetails.website}
            onChange={(e) => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, website: e.target.value } }))}
            placeholder="https://instagram.com/..."
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${errors.website ? "border-red-400" : "border-gray-300"}`}
          />
          {errors.website && <p className="text-xs text-red-500 mt-1">{errors.website}</p>}
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
        <Field label="Years of Experience" required>
          <div className="inline-flex items-center gap-3">
            <button
              onClick={() => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, experience: Math.max(0, parseInt(prev.businessDetails.experience) - 1).toString() } }))}
              className="w-10 h-10 bg-orange-500 text-white rounded-lg"
            >
              -
            </button>
            <div className="text-lg font-semibold">{formData.businessDetails.experience}</div>
            <button
              onClick={() => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, experience: (parseInt(prev.businessDetails.experience) + 1).toString() } }))}
              className="w-10 h-10 bg-orange-500 text-white rounded-lg"
            >
              +
            </button>
          </div>
        </Field>

        <Field label="GSTIN (Optional)">
          <input
            value={formData.businessDetails.gstin}
            onChange={(e) => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, gstin: e.target.value } }))}
            placeholder="XXXXX XXXXX XXXXX"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none border-gray-300"
          />
        </Field>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Venue Location <span className="text-red-500">*</span></label>
          <button
            onClick={() => detectLocation("venue", setFormData, () => { }, showToast)}
            className="inline-flex items-center gap-2 text-sm px-3 py-1 rounded bg-orange-50 text-orange-600"
          >
            <MapPin size={14} /> Detect Venue Location
          </button>
        </div>
        <textarea
          ref={venueRef}
          defaultValue={formData.businessDetails.venueLocation.address}
          onBlur={syncVenue}
          rows={3}
          placeholder="Venue address"
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${errors.venueLocation ? "border-red-400" : "border-gray-300"}`}
        />
        {errors.venueLocation && <p className="text-xs text-red-500 mt-1">{errors.venueLocation}</p>}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          value={formData.businessDetails.venueLocation.city}
          onChange={(e) => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, venueLocation: { ...prev.businessDetails.venueLocation, city: e.target.value } } }))}
          placeholder="City"
          className="px-3 py-2 border rounded-lg focus:outline-none border-gray-300"
        />
        <input
          value={formData.businessDetails.venueLocation.state}
          onChange={(e) => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, venueLocation: { ...prev.businessDetails.venueLocation, state: e.target.value } } }))}
          placeholder="State"
          className="px-3 py-2 border rounded-lg focus:outline-none border-gray-300"
        />
        <input
          value={formData.businessDetails.venueLocation.country}
          onChange={(e) => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, venueLocation: { ...prev.businessDetails.venueLocation, country: e.target.value } } }))}
          placeholder="Country"
          className="px-3 py-2 border rounded-lg focus:outline-none border-gray-300"
        />
      </div>

      <div className="mt-4">
        <label className="inline-flex items-center gap-3">
          <input
            type="checkbox"
            checked={formData.businessDetails.openForRelocation}
            onChange={(e) => setFormData((prev) => ({ ...prev, businessDetails: { ...prev.businessDetails, openForRelocation: e.target.checked } }))}
            className="w-4 h-4 rounded border-gray-300 accent-orange-500"
          />
          <span className="text-sm text-gray-700">Open for relocation to other cities</span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <Field label="About Your Business" required>
          <textarea
            defaultValue={formData.aboutBusiness}
            onBlur={(e) => setFormData((prev) => ({ ...prev, aboutBusiness: e.target.value }))}
            rows={4}
            placeholder="Brief description"
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${errors.aboutBusiness ? "border-red-400" : "border-gray-300"}`}
          />
          {errors.aboutBusiness && <p className="text-xs text-red-500 mt-1">{errors.aboutBusiness}</p>}
        </Field>

        <Field label="Offers (Optional)">
          <textarea
            defaultValue={formData.offers}
            onBlur={(e) => setFormData((prev) => ({ ...prev, offers: e.target.value }))}
            rows={4}
            placeholder="Offers (one per line)"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none border-gray-300"
          />
        </Field>
      </div>

      <div className="mt-4 flex gap-3 justify-end">
        <button onClick={() => next(2)} className="px-4 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50">
          Previous
        </button>
        <button
          onClick={() => {
            const e = {};
            if (!formData.businessDetails.businessName.trim()) e.businessName = "Business name required";
            if (!formData.businessDetails.website.trim()) e.website = "Website/Instagram required";
            if (!formData.businessDetails.venueLocation.address.trim()) e.venueLocation = "Venue address required";
            if (!formData.aboutBusiness.trim()) e.aboutBusiness = "About required";
            if (Object.keys(e).length) {
              setErrors(e);
              alert("Please fill all required business details");
              return;
            }
            setErrors({});
            next(4);
          }}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Save & Continue
        </button>
      </div>
    </motion.div>
  );
};

// Service Listing (Portfolio + Venues)
const ServiceListingScreen = ({
  formData,
  setFormData,
  isSubmitting,
  uploadProgress,
  setIsSubmitting,
  setUploadProgress,
  handleSubmitFinal,
  next,
}) => {
  const [newVenue, setNewVenue] = useState({ name: "", price: "", description: "" });
  const [showVenueForm, setShowVenueForm] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState("");
  const albums = ["Wedding", "Corporate", "Birthday", "Other"];

  const addVenue = () => {
    if (!newVenue.name.trim()) {
      alert("Enter venue name");
      return;
    }
    setFormData((prev) => ({ ...prev, serviceVenues: [...prev.serviceVenues, newVenue] }));
    setNewVenue({ name: "", price: "", description: "" });
    setShowVenueForm(false);
  };

  const removeVenue = (idx) => setFormData((prev) => ({ ...prev, serviceVenues: prev.serviceVenues.filter((_, i) => i !== idx) }));

  const removePortfolioImage = (idx) => setFormData((prev) => ({ ...prev, portfolioImages: prev.portfolioImages.filter((_, i) => i !== idx) }));

  const handlePortfolioImages = (e) => {
    if (!selectedAlbum) {
      alert("Please select an album first");
      return;
    }
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large. Max 5MB`);
        return false;
      }
      return true;
    });
    const newImages = validFiles.map((file) => ({ file, preview: URL.createObjectURL(file), albumName: selectedAlbum }));
    setFormData((prev) => ({ ...prev, portfolioImages: [...prev.portfolioImages, ...newImages] }));
  };

  return (
    <motion.div
      key="listing"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -8 }}
      className="max-w-6xl mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-10"
    >
      <h3 className="text-xl font-bold text-orange-500 mb-2">Portfolio & Pricing</h3>
      <p className="text-gray-600 mb-4">Add venues and upload photos from your events.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Venues */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-semibold text-gray-800">Venues & Prices</h4>
              <p className="text-sm text-gray-500">Add the venue types you offer</p>
            </div>
            <button onClick={() => setShowVenueForm((s) => !s)} className="text-sm text-orange-500">
              {showVenueForm ? "Close" : "+ Add New"}
            </button>
          </div>

          {showVenueForm && (
            <div className="border rounded-lg p-3 mb-3">
              <input value={newVenue.name} onChange={(e) => setNewVenue((s) => ({ ...s, name: e.target.value }))} placeholder="Venue name *" className="w-full px-3 py-2 border rounded mb-2" />
              <input value={newVenue.price} onChange={(e) => setNewVenue((s) => ({ ...s, price: e.target.value }))} placeholder="Starting price (₹)" className="w-full px-3 py-2 border rounded mb-2" />
              <textarea value={newVenue.description} onChange={(e) => setNewVenue((s) => ({ ...s, description: e.target.value }))} placeholder="Description (optional)" rows={2} className="w-full px-3 py-2 border rounded mb-2" />
              <div className="flex gap-2">
                <button onClick={addVenue} className="flex-1 bg-orange-500 text-white py-2 rounded">Add</button>
                <button onClick={() => setShowVenueForm(false)} className="flex-1 border rounded py-2">Cancel</button>
              </div>
            </div>
          )}

          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {formData.serviceVenues.length === 0 && <div className="text-sm text-gray-500">No venues added yet.</div>}
            {formData.serviceVenues.map((v, i) => (
              <div key={i} className="border rounded p-3 flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{v.name}</div>
                  {v.price && <div className="text-sm text-orange-500 mt-1">₹{v.price}</div>}
                  {v.description && <div className="text-sm text-gray-500 mt-1">{v.description}</div>}
                </div>
                <button onClick={() => removeVenue(i)} className="text-red-500"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Portfolio */}
        <div>
          <div className="mb-3">
            <h4 className="font-semibold text-gray-800">Showcase Your Work</h4>
            <p className="text-sm text-gray-500">Upload images categorized by album</p>
          </div>

          <div className="mb-3">
            <select value={selectedAlbum} onChange={(e) => setSelectedAlbum(e.target.value)} className="w-full px-3 py-2 border rounded">
              <option value="">Select album</option>
              {albums.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <label className="w-full border-2 border-dashed border-orange-300 rounded-lg p-6 text-center cursor-pointer hover:bg-orange-50 block">
            <Upload size={26} className="mx-auto mb-2 text-orange-500" />
            <div className="text-orange-500 font-medium">Click to upload images</div>
            <div className="text-xs text-gray-400">(Max 5MB per image)</div>
            <input type="file" accept="image/*" multiple onChange={handlePortfolioImages} className="hidden" />
          </label>

          <div className="grid grid-cols-3 gap-3 mt-3 max-h-64 overflow-y-auto pr-1">
            {formData.portfolioImages.map((img, i) => (
              <div key={i} className="border rounded p-2 relative">
                <img src={img.preview} alt="portfolio" className="w-full h-24 object-cover rounded" />
                <div className="mt-2 text-xs truncate">{img.albumName}</div>
                <div className="absolute top-2 right-2">
                  <button onClick={() => removePortfolioImage(i)} className="bg-white rounded-full p-1 shadow">
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {formData.portfolioImages.length > 0 && <div className="text-sm text-gray-500 mt-2">{formData.portfolioImages.length} image(s) selected</div>}
        </div>
      </div>

      {isSubmitting && (
        <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200 flex items-center gap-3">
          <Loader className="animate-spin text-orange-500" size={18} />
          <div className="text-sm text-orange-700">{uploadProgress || "Submitting..."}</div>
        </div>
      )}

      <div className="mt-6 flex gap-3 justify-end">
        <button onClick={() => next(3)} disabled={isSubmitting} className="px-4 py-2 border border-orange-500 text-orange-500 rounded-lg">
          Previous
        </button>
        <button disabled={isSubmitting} onClick={handleSubmitFinal} className="px-4 py-2 bg-orange-500 text-white rounded-lg">
          {isSubmitting ? (<><Loader className="animate-spin" size={14} /> Submitting...</>) : "Submit Registration"}
        </button>
      </div>
    </motion.div>
  );
};

// Final screen
const RegistrationDone = ({ reset }) => (
  <motion.div key="done" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
    <div className="mb-4">
      <svg className="w-48 h-auto mx-auto" viewBox="0 0 400 300" fill="none" aria-hidden>
        <rect x="150" y="200" width="100" height="3" fill="#D4A574" />
        <circle cx="230" cy="90" r="18" fill="#FF9500" />
      </svg>
    </div>
    <h2 className="text-2xl font-bold text-orange-500 mb-2">Hurray — registration completed!</h2>
    <p className="text-gray-600 mb-6">Your profile is saved. Log in to access your dashboard and wallet.</p>
    <button onClick={reset} className="bg-orange-500 text-white py-2 px-6 rounded-lg">Back to Home</button>
  </motion.div>
);

/* -------------------------
   Top-level wizard component
--------------------------*/

export default function PartnerRegistrationForm() {
  const steps = ["Welcome", "Basic", "Services", "Business", "Portfolio", "Done"];

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    agreedToTerms: false,
    fullName: "",
    email: "",
    gender: "",
    mobile: "",
    image: null,
    imagePreview: "",
    location: { address: "", city: "", state: "", country: "", latitude: null, longitude: null },
    selectedServices: [],
    businessDetails: {
      businessName: "",
      experience: "0",
      gstin: "",
      website: "",
      venueLocation: { address: "", city: "", state: "", country: "", latitude: null, longitude: null },
      openForRelocation: false,
    },
    aboutBusiness: "",
    offers: "",
    policies: "",
    serviceVenues: [],
    portfolioImages: [],
  });

  const services = [
    { id: "venue", title: "Event Venue", desc: "Banquet, Resort, Lawn", icon: "🏨" },
    { id: "photography", title: "Photography", desc: "Wedding, Events, Products", icon: "📷" },
    { id: "videography", title: "Videography", desc: "Cinematic videos", icon: "🎥" },
    { id: "decoration", title: "Decoration", desc: "Stage & Venue decor", icon: "🎉" },
    { id: "makeup", title: "Makeup Artist", desc: "Bridal, Party, Groom", icon: "💄" },
    { id: "dj", title: "DJ & Sound", desc: "Music, Lights & Sound", icon: "🎧" },
  ];

  // simple toast substitute (user can replace w/ real toast lib)
  const showToast = (msg, type = "info") => {
    // For now: quick browser alert only for errors / warnings
    if (type === "error" || type === "warning") alert(msg);
    else {
      // silent success for UX; could replace with toast
      // console.log(type, msg);
    }
  };

  const next = (stepIndex) => setCurrentStep(stepIndex);

  // Final submission logic (mirrors your original)
  const handleSubmitFinal = async () => {
    try {
      setIsSubmitting(true);
      setUploadProgress("Authenticating...");
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        alert("Please login again before submitting your details.");
        setIsSubmitting(false);
        return;
      }

      // upload profile image
      let profileImageUrl = "";
      if (formData.image) {
        setUploadProgress("Uploading profile image...");
        profileImageUrl = await uploadImageToStorage(formData.image, `vendors/${user.uid}/profile`);
      }

      // upload portfolio images sequentially
      const portfolioUrls = [];
      if (formData.portfolioImages.length > 0) {
        setUploadProgress("Uploading portfolio images...");
        for (let img of formData.portfolioImages) {
          const url = await uploadImageToStorage(img.file, `vendors/${user.uid}/portfolio`);
          portfolioUrls.push({ albumName: img.albumName, imageUrl: url });
        }
      }

      setUploadProgress("Saving vendor data...");

      const vendorData = {
        userId: user.uid,
        name: formData.fullName,
        email: formData.email,
        gender: formData.gender,
        number: formData.mobile,
        image: profileImageUrl,
        location: formData.location,
        partnerType: formData.selectedServices.join(", "),
        businessDetails: formData.businessDetails,
        aboutBusiness: formData.aboutBusiness,
        offers: formData.offers,
        policies: formData.policies,
        serviceVenues: formData.serviceVenues,
        portfolioImages: portfolioUrls,
        rating: 0,
        isPartner: true,
        fcmToken: "",
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "vendors"), vendorData);

      setUploadProgress("");
      setIsSubmitting(false);
      setCurrentStep(5);
      alert("Registration completed successfully");
    } catch (err) {
      console.error("Submit error:", err);
      alert(err.message || "Something went wrong");
      setIsSubmitting(false);
      setUploadProgress("");
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      agreedToTerms: false,
      fullName: "",
      email: "",
      gender: "",
      mobile: "",
      image: null,
      imagePreview: "",
      location: { address: "", city: "", state: "", country: "", latitude: null, longitude: null },
      selectedServices: [],
      businessDetails: {
        businessName: "",
        experience: "0",
        gstin: "",
        website: "",
        venueLocation: { address: "", city: "", state: "", country: "", latitude: null, longitude: null },
        openForRelocation: false,
      },
      aboutBusiness: "",
      offers: "",
      policies: "",
      serviceVenues: [],
      portfolioImages: [],
    });
    setCurrentStep(0);
    setErrors({});
  };

  // Layout
  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar (desktop) */}
      <Sidebar steps={steps} currentStep={currentStep} />

      {/* Main container */}
      <main className="flex-1 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Top header for mobile (compact) */}
          <div className="md:hidden flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {/* <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center shadow">
                <Menu size={18} />
              </div> */}
              <div>
                <div className="text-sm font-semibold text-orange-500">Partner Registration</div>
                {/* <div className="text-xs text-gray-500">Partner Registration</div> */}
              </div>
            </div>
            <div className="text-sm text-gray-500">Step {currentStep + 1} / {steps.length}</div>
          </div>

          {/* Step content */}
          <div className="pb-24"> {/* spacing for mobile bottom stepper */}
            <AnimatePresence mode="wait" initial={false}>
              {currentStep === 0 && (
                <WelcomeScreen formData={formData} setFormData={setFormData} next={next} />
              )}

              {currentStep === 1 && (
                <BasicDetailsScreen
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  next={next}
                  detectLocation={detectAndFillLocation}
                  showToast={showToast}
                />
              )}

              {currentStep === 2 && (
                <ServiceDetailsScreen formData={formData} setFormData={setFormData} services={services} next={next} />
              )}

              {currentStep === 3 && (
                <BusinessInformationScreen
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  next={next}
                  detectLocation={detectAndFillLocation}
                  showToast={showToast}
                />
              )}

              {currentStep === 4 && (
                <ServiceListingScreen
                  formData={formData}
                  setFormData={setFormData}
                  isSubmitting={isSubmitting}
                  uploadProgress={uploadProgress}
                  setIsSubmitting={setIsSubmitting}
                  setUploadProgress={setUploadProgress}
                  handleSubmitFinal={handleSubmitFinal}
                  next={next}
                />
              )}

              {currentStep === 5 && <RegistrationDone reset={resetForm} />}
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* Mobile bottom stepper */}
      <MobileStepper steps={steps.slice(0, 5)} currentStep={currentStep} onJump={(i) => setCurrentStep(i)} />

      {/* small fixed success indicator on desktop when done */}
      {currentStep === 5 && (
        <div className="hidden md:flex fixed right-6 bottom-6 items-center gap-3 bg-white border rounded-full px-4 py-2 shadow">
          <Check className="text-green-500" />
          <div className="text-sm">Registration complete</div>
        </div>
      )}
    </div>
  );
}
